import { Box, SvgIcon } from '@mui/material';
import BoltIcon from 'assets/icons/bolt.svg';
import StarSignIcon from 'assets/icons/star-sign.svg';
import StarIcon from 'assets/icons/star.svg';
import React from 'react';
import {
  getDigitalSignatureCardBody,
  getDigitalSignatureCardTitle,
} from 'utils/getDigitalSignatureCardCopy';
import { I18n } from 'utils/i18n';
import messageShape from 'shapes/message';
import { useSelector } from 'react-redux';
import authEmployee from 'store/selectors/authEmployee';
import { isSignatureDismissed } from 'components/@home/@signatures/pendingSignatures';
import AcknowledgeCaption from './AcknowledgeCaption';
import MonitorButton from './MonitorButton';
import OpenButton from './OpenButton';

const SIGNER_RANK = { pending: 1, sent: 1, viewed: 2, expired: 3, declined: 4, signed: 5 };

function ownSignerStatus(message, userId) {
  let best = null;
  let rank = 0;
  (message.signatureRequests || []).forEach(item => {
    if (String(item.user?._id || item.user) !== String(userId)) return;
    const next = SIGNER_RANK[item.status] || 0;
    if (next < rank) return;
    rank = next;
    best = item.status === 'sent' ? 'pending' : item.status;
  });
  return best;
}

const Read = ({ message }) => {
  const employee = useSelector(authEmployee);
  const userId = employee?.user?._id || employee?.user;
  const closed = ['declined', 'expired'].includes(ownSignerStatus(message, userId));
  const blocked = isSignatureDismissed(message._id) || message.signatureStatus === 'not_applicable';
  const declinedAt = (message.signatureRequests || []).find(
    item =>
      String(item.user?._id || item.user) === String(userId) &&
      item.status === 'declined' &&
      item.declinedAt,
  )?.declinedAt;

  let Icon = StarIcon;
  let subheading = I18n.t('AcknowledgementMessage.MANDATORY READ');

  if (message.digitalSignature) {
    Icon = StarSignIcon;
    subheading = I18n.t('AcknowledgementMessage.DIGITAL SIGNATURE');
  } else if (message.form) {
    Icon = BoltIcon;
    subheading = I18n.t('AcknowledgementMessage.FORM');
  }

  const cardTitle = message.digitalSignature ? getDigitalSignatureCardTitle(message) : message.text;
  const cardBody = message.digitalSignature ? getDigitalSignatureCardBody(message) : '';

  return (
    <Box
      sx={theme => ({
        position: 'relative',
        padding: '30px 20px',
        minHeight: 200,
        boxShadow: '0 7px 22px 0 rgba(2, 2, 3, 0.13)',
        border: 'solid 1px rgba(221, 226, 235, 0.1)',
        minWidth: '260px',
        maxWidth: '500px',
        borderRadius: '15px',
        display: 'flex',
        justifyContent: 'center',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.paper,
        alignItems: 'center',
        boxSizing: 'border-box',
        ...(cardBody && {
          pb: 3.5,
        }),
      })}
    >
      <SvgIcon sx={{ color: 'primary.main', mb: '10px' }}>
        <Icon />
      </SvgIcon>
      <Box
        component="span"
        sx={{
          fontSize: '12px',
          fontWeight: '500',
          textTransform: 'uppercase',
          mb: '5px',
          color: 'primary.main',
        }}
      >
        {subheading}
      </Box>
      <Box
        component="h3"
        sx={theme => ({
          textAlign: 'center',
          flex: '0 0 auto',
          width: '100%',
          maxWidth: '100%',
          display: 'block',
          fontSize: '20px',
          m: '0 0 8px',
          px: 0.5,
          fontWeight: 'bold',
          color: theme.palette.primary.ultraDark,
          boxSizing: 'border-box',
          wordBreak: 'break-word',
        })}
      >
        {cardTitle}
      </Box>
      {cardBody ? (
        <Box
          sx={{
            textAlign: 'center',
            width: '100%',
            alignSelf: 'stretch',
            mb: 1.5,
            px: 1,
            boxSizing: 'border-box',
          }}
        >
          <Box
            component="p"
            sx={{
              display: 'inline-flex',
              width: 'auto',
              maxWidth: '100%',
              textAlign: 'left',
              fontSize: '14px',
              lineHeight: 1.45,
              m: 0,
              color: 'text.secondary',
              whiteSpace: 'pre-wrap',
              boxSizing: 'border-box',
            }}
          >
            {cardBody}
          </Box>
        </Box>
      ) : null}
      <Box
        sx={{
          display: 'flex',
          flex: '0 0 auto',
          gap: 1,
          mt: 0.5,
          pt: 0.5,
        }}
      >
        {!blocked &&
          !closed &&
          (!message.isDirect ||
            !message.digitalSignature ||
            message.signingMode !== 'onePerMember' ||
            !message.isMine) && <OpenButton message={message} />}
        <MonitorButton message={message} />
      </Box>
      <AcknowledgeCaption
        form={message.form}
        digitalSignature={message.digitalSignature}
        acknowledges={message.acknowledges}
        declinedAt={declinedAt}
      />
    </Box>
  );
};

Read.propTypes = {
  message: messageShape.isRequired,
};

export default Read;
